import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiCheck, FiPlus, FiSearch, FiX } from "react-icons/fi";
import { highlightParts } from "./panelModel";
import "./panels.scss";

// Panel lateral derecho común a Columnas y Filtros: cabecera con buscador fijo,
// cuerpo con scroll y pie de acciones. Se monta en <body> para no depender del
// overflow ni del apilado de la barra de herramientas. Esc cierra (si el buscador
// tiene texto, el primer Esc solo lo vacía).
export function SidePanel({ title, subtitle, icon, onClose, searchValue, onSearchChange, searchPlaceholder, onSearchEnter, toolbar, footer, children, ariaLabel }) {
    const searchRef = useRef(null);
    const panelRef = useRef(null);
    // El botón que abrió el panel se lee en el primer render: los efectos de los hijos
    // (editores con autofoco) corren antes que el de este componente.
    const [opener] = useState(() => document.activeElement);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Solo al abrir: foco en el buscador y Esc para cerrar (onClose cambia de
    // identidad en cada render del padre; no debe volver a robar el foco). Al
    // cerrar, el foco vuelve al botón que abrió el panel.
    useEffect(() => {
        if (!panelRef.current?.contains(document.activeElement)) searchRef.current?.focus({ preventScroll: true });
        const onKey = (event) => {
            if (event.key === "Escape") { onCloseRef.current(); return; }
            // Trampa de foco: Tab no sale del panel (el resto de la página queda detrás del velo).
            if (event.key !== "Tab" || !panelRef.current) return;
            const focusable = Array.from(panelRef.current.querySelectorAll("button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex='-1'])"))
                .filter((node) => node.offsetParent !== null || node === document.activeElement);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (!panelRef.current.contains(document.activeElement)) { event.preventDefault(); first.focus(); }
            else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        window.addEventListener("keydown", onKey);
        return () => {
            window.removeEventListener("keydown", onKey);
            if (opener && typeof opener.focus === "function" && document.contains(opener)) opener.focus({ preventScroll: true });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return createPortal(
        <div className="obras-panel-layer">
            <div className="obras-panel-backdrop" onClick={onClose} />
            <aside ref={panelRef} className="obras-panel" role="dialog" aria-modal="true" aria-label={ariaLabel || title}>
                <header className="obras-panel__head">
                    <div className="obras-panel__title">
                        {icon ? <span className="obras-panel__icon">{icon}</span> : null}
                        <div>
                            <strong>{title}</strong>
                            {subtitle ? <span aria-live="polite">{subtitle}</span> : null}
                        </div>
                        <button type="button" className="obras-panel__close" aria-label="Cerrar" onClick={onClose}><FiX /></button>
                    </div>
                    <SearchField
                        inputRef={searchRef}
                        value={searchValue}
                        onChange={onSearchChange}
                        placeholder={searchPlaceholder}
                        onEnter={onSearchEnter}
                    />
                    {toolbar}
                </header>
                <div className="obras-panel__body">{children}</div>
                {footer ? <footer className="obras-panel__foot">{footer}</footer> : null}
            </aside>
        </div>,
        document.body
    );
}

export function SearchField({ value, onChange, placeholder, onEnter, inputRef, small = false, ariaLabel }) {
    return (
        <div className={`obras-panel-search${small ? " obras-panel-search--small" : ""}`}>
            <FiSearch aria-hidden="true" />
            <input
                ref={inputRef}
                type="search"
                value={value}
                placeholder={placeholder}
                aria-label={ariaLabel || placeholder}
                onChange={(event) => onChange(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === "Escape" && value) { event.stopPropagation(); event.nativeEvent.stopImmediatePropagation(); onChange(""); }
                    if (event.key === "Enter" && onEnter) { event.preventDefault(); onEnter(); }
                }}
            />
            {value ? <button type="button" aria-label="Vaciar búsqueda" onClick={() => { onChange(""); inputRef?.current?.focus(); }}><FiX /></button> : null}
        </div>
    );
}

// Texto con las coincidencias de la búsqueda resaltadas.
export function Highlight({ text, query }) {
    return highlightParts(text, query).map((part, index) => (part.match
        ? <mark key={index}>{part.text}</mark>
        : <React.Fragment key={index}>{part.text}</React.Fragment>));
}

// Fila de vistas guardadas: chips para aplicar, × para borrar las propias y
// "Guardar vista" con nombre en línea. saveStatus(name) → { canSave, message }.
export function ViewsBar({ label = "Vistas", views, activeId, onApply, onDelete, onSave, saveStatus, maxNameLength, emptyHint }) {
    const [naming, setNaming] = useState(false);
    const [name, setName] = useState("");
    const status = saveStatus(name.trim());
    const submit = (event) => {
        event.preventDefault();
        if (!status.canSave) return;
        onSave(name.trim());
        setName("");
        setNaming(false);
    };
    return (
        <section className="obras-panel-views" aria-label={label}>
            <div className="obras-panel-views__row">
                <span className="obras-panel-eyebrow">{label}</span>
                <div className="obras-panel-views__chips">
                    {views.map((view) => (
                        <span key={view.id} className={`obras-panel-chip${activeId === view.id ? " is-active" : ""}`}>
                            <button type="button" aria-pressed={activeId === view.id} onClick={() => onApply(view)} title={view.builtin ? "Vista predefinida" : "Vista guardada"}>
                                {activeId === view.id ? <FiCheck aria-hidden="true" /> : null}
                                {view.label}
                            </button>
                            {!view.builtin ? (
                                <button type="button" className="obras-panel-chip__del" aria-label={`Eliminar vista ${view.label}`} onClick={() => onDelete(view.id)}><FiX /></button>
                            ) : null}
                        </span>
                    ))}
                    {!views.length && emptyHint ? <span className="obras-panel-views__empty">{emptyHint}</span> : null}
                    {!naming ? (
                        <button type="button" className="obras-panel-chip obras-panel-chip--add" onClick={() => setNaming(true)}>
                            <FiPlus aria-hidden="true" /> Guardar actual
                        </button>
                    ) : null}
                </div>
            </div>
            {naming ? (
                <form
                    className="obras-panel-views__namer"
                    onSubmit={submit}
                    onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); event.nativeEvent.stopImmediatePropagation(); setNaming(false); setName(""); } }}
                >
                    <input
                        autoFocus
                        type="text"
                        value={name}
                        maxLength={maxNameLength}
                        placeholder="Nombre de la vista"
                        aria-label="Nombre de la vista"
                        onChange={(event) => setName(event.target.value)}
                    />
                    <button type="submit" className="obras-panel-btn obras-panel-btn--primary" disabled={!status.canSave}>Guardar</button>
                    <button type="button" className="obras-panel-btn" onClick={() => { setNaming(false); setName(""); }}>Cancelar</button>
                    {status.message ? <p className="obras-panel-views__msg">{status.message}</p> : null}
                </form>
            ) : null}
        </section>
    );
}
